import React, { useState, useEffect, useMemo, useContext,useCallback, useRef } from "react";
import "./DynamicTable1.css";
import { ContextEngine } from "../../../engine/ContextEngine";
import { EventsRegistry } from "../../../engine/events/index";
import { triggerEvent } from "../../../engine/events/bus";
import useColumnResize from "../../../engine/hooks/useColumnResize";

import {useTableSelection} from "../../../engine/hooks/useTableSelection";
import {useTableData} from "../../../engine/hooks/useTableData";

import { getSortedData, getPaginatedData } from "../../../utils/tableUtils";
import TablePagination from "../../TablePagination";
import TableHeaderFilter from "./TableHeaderFilter";
import TableCell from "./TableCell";
import "./TableCell.css";

const CHECKBOX_WIDTH = 40;

const DynamicTable = ({ centralUnit }) => {
  const { cvl = [], data = [], id: tableId = "" } = centralUnit || {};
  const { context, updateContext } = useContext(ContextEngine);
  // const { loading, error } = useTableData(); // The hook handles the "when" and "how"
  // if (loading) return <div className="spinner">Loading {context.view.activeSheet}...</div>;
  // if (error) return <div className="error">{error}</div>;

  // const { rows, isLoading, isPlaceholderData } = useSheetData(activeSheetId, currentPage);

  // if (isLoading) return <div className="spinner">Loading...</div>;

  // return (
  //   <div style={{ opacity: isPlaceholderData ? 0.5 : 1 }}>
  //     {/* Render your rows here */}
  //     <button onClick={() => setPage(p => p + 1)}>Next Page</button>
  //   </div>
  // );

  
  
  const checkboxRef = useRef();
  const tableContainerRef = useRef(null);

  const unitIndex = context.context_units?.findIndex(u => u.id === tableId);
  const currentUnit = context.context_units[unitIndex];
  const view = currentUnit?.view || { page: 1, pageSize: 10 };
  const filters = view.filters || {};

  const tableColumns = [
    { name: "__checkbox__", type: "checkbox" },
    ...(currentUnit?.cvl || [])
  ];

  // Add this helper inside your component
const isSelected = (rowId, colName) => {
  const selectedCells = Array.isArray(view.selectedCells) ? view.selectedCells : [];
  return selectedCells.some(c => c.rowId === rowId && c.colName === colName);
};
// Get the first selected cell to track "where we are"
const activeCell = (view.selectedCells && view.selectedCells.length > 0) 
  ? view.selectedCells[0] 
  : null;



  const openFilterMenu = (colName, e) => {
    e.preventDefault(); // Stop the default browser menu
    const sourceData = currentUnit?.data || data || [];
    
    // 1. Collect unique values from the column for the filter list
    // We use displayedData to ensure we only filter based on what's visible
    const uniqueValues = Array.from(
      new Set(sourceData.map(item => String(item.row[colName] ?? "")))
    ).filter(v => v !== "");

    // 2. Open the modal via the ContextEngine
    updateContext("view.modal", {
      type: "FILTER_MENU", // Ensure this matches your ModalRegistry key
      props: {
        unitId: tableId, 
        colName: colName,
        options: uniqueValues,
        position: { 
          x: e.clientX, 
          y: e.clientY 
        }
      }
    });
  };

  const cellRefs = useRef([]); // 2D array: cellRefs[row][col]

    // --- 1. Define the Blur Handler ---
  const handleRowBlur = (rowId) => {
    // Find the specific object by its row_id in the current unit data
    const entry = currentUnit?.data?.find(i => i.row_id === rowId);
    
    if (!entry._dirty) return; // only commit if values changed
    triggerEvent(EventsRegistry.TABLE_ROW_COMMITTED, { rowId, rowData: entry });
  };

    // --- CLEAR ALL FILTERS ---
  const handleClear = () => {
    if (unitIndex !== -1) {
      // Direct path to the filters object for this specific table
      const path = `context_units.${unitIndex}.view.filters`;
      
      console.log("%c🧹 Clearing All Filters", "color: #e67e22; font-weight: bold;");
      
      // Update the context to an empty object
      updateContext(path, {});
    }
  };

  // --- 1. SORTING ---
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const handleSort = (colName) => {
    let direction = "asc";
    if (sortConfig.key === colName && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key: colName, direction });
  };

  // --- 2. STICKY & RESIZING ---
  const [colWidths, setColWidths] = useState(
    cvl.map((c, idx) => (c.type === "buttonList" ? 150 : 120))
  );
  

  const { startResize } = useColumnResize(tableId, colWidths, setColWidths);

  // Offset stickyLeft by the checkbox width
  const stickyLeft = useMemo(() => {
    let acc = CHECKBOX_WIDTH; 
    return colWidths.map(w => { 
      let left = acc; 
      acc += w; 
      return left; 
    });
  }, [colWidths]);

  // --- 3. DATA PIPELINE (Filter -> Sort -> Paginate) ---
  const displayedData = useMemo(() => {
    const topSearch = view.search || "";
    const sourceData = currentUnit?.data || data || [];

    const filtered = sourceData.filter(item => {
      const matchesFilters = cvl.every((col) => {
        const f = filters[col.name];
        if (!f) return true;
        const cellValue = String(item.row[col.name] || "");
        return f.includes("|") 
          ? f.split("|").some(opt => cellValue === opt)
          : cellValue.toLowerCase().includes(f.toLowerCase());
      });

      const matchesSearch = !topSearch || Object.values(item.row).some(cell => 
        String(cell).toLowerCase().includes(topSearch.toLowerCase())
      );
      return matchesFilters && matchesSearch;
    });

    return getSortedData(filtered, sortConfig.key, sortConfig.direction);
  }, [currentUnit?.data, filters, view.search, cvl, sortConfig]);

  const paginatedData = useMemo(() => {
    return getPaginatedData(displayedData, view.page - 1, view.pageSize);
  }, [displayedData, view.page, view.pageSize]);

  // --- 4. SELECTION LOGIC ---
  const isAllSelected = paginatedData.length > 0 && paginatedData.every(i => i.meta?.selected);
  const isIndeterminate = paginatedData.some(i => i.meta?.selected) && !isAllSelected;



const { handleCellClick, handleTableKeyDown,handleBulkReplace } = useTableSelection({
  data: paginatedData,
  columns: cvl,
  updateContext,
  unitIndex,
  selectedCells: view.selectedCells || []
});


const toggleAllSelection = (isSelected) => {
  const updatedData = currentUnit.data.map(item => ({
    ...item,
    meta: { 
      ...(item.meta || {}), // Preserve existing meta like 'hidden' or 'locked'
      selected: isSelected 
    }
  }));
  
  updateContext(`context_units.${unitIndex}.data`, updatedData);
};


  // --- 5. AGGREGATIONS ---
const totals = useMemo(() => {
  return cvl.map((col) => {
    if (col.agg === "sum") {
      const sum = displayedData.reduce((acc, item) => acc + (Number(item.row?.[col.name]) || 0), 0);
      return sum;
    }
    return col.agg === "count" ? displayedData.length : "";
  });
}, [cvl, displayedData]);

const toggleRowSelection = (rowId) => {
  // 1. Find the index of the row we want to toggle
  const rowIndex = currentUnit.data.findIndex(r => r.row_id === rowId);
  
  if (rowIndex === -1) return;

  // 2. Get the current status (default to false if meta doesn't exist)
  const currentStatus = currentUnit.data[rowIndex].meta?.selected || false;
  
  // 3. Define the path to that specific row's metadata
  const path = `context_units.${unitIndex}.data.${rowIndex}.meta.selected`;
  
  // 4. Update just that one value
  updateContext(path, !currentStatus);
};

useEffect(() => {
  const handleClickOutside = (e) => {
    // If the click is NOT inside the table container, clear selection
    if (tableContainerRef.current && !tableContainerRef.current.contains(e.target)) {
      updateContext(`context_units.${unitIndex}.view.selectedCells`, []);
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, [unitIndex, updateContext]);


  useEffect(() => {
    if (checkboxRef.current) checkboxRef.current.indeterminate = isIndeterminate;
  }, [isIndeterminate]);

  return (
    <div className="dynamic-table-container" 
        onKeyDown={(e) => handleTableKeyDown(e, activeCell)}
        tabIndex="0" // Makes the div focusable so it catches key events        
        style={{ height: '100%', width: '100%', overflow: 'auto', outline:'none'
     }}>
      <table className="dynamic-table"
      style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}
      >
        
        <thead style={{ zIndex: 50 }}>
          {/* Row 1: Labels & Sort */}
          <tr style={{ height: '40px' }}>
            <th className="sticky-col" style={{ width: CHECKBOX_WIDTH, position: 'sticky', left: 0, top: 0, zIndex: 60, backgroundColor: '#eee', borderBottom: '1px solid #ccc',
                /* --- SYNC TO FIXED WIDTH --- */
                '--col-width': `${CHECKBOX_WIDTH}px`,
                '--col-left': '0px'
             }}>
              <input type="checkbox" ref={checkboxRef} checked={isAllSelected} onChange={(e) => toggleAllSelection(e.target.checked)} />
            </th>
            {cvl.map((col, idx) => {
              const isSticky = idx < 3;
              return (
                <th key={col.name} className={isSticky ? "sticky-col" : ""} 
                  onClick={() => handleSort(col.name)}
                  onContextMenu={(e) => openFilterMenu(col.name, e)}
                  style={{
                            /* --- SYNC TO CSS VARIABLES --- */
                    '--col-width': `${colWidths[idx]}px`,
                    '--col-left': isSticky ? `${stickyLeft[idx]}px` : 'auto',
                    width: colWidths[idx], position: 'sticky', top: 0,
                    left: isSticky ? stickyLeft[idx] : 'auto',
                    zIndex: isSticky ? 55 : 50, backgroundColor: '#eee', borderBottom: '1px solid #ccc', padding: '8px', cursor: 'pointer'
                  }}
                >
                  {col.name} {sortConfig.key === col.name && (sortConfig.direction === "asc" ? "↑" : "↓")}
                  <div className="col-resizer" onMouseDown={e => { e.stopPropagation(); startResize(e, idx); }} />
                </th>
              );
            })}
          </tr>

          {/* Row 2: Filters */}
          <tr style={{ height: '40px' }}>

            {/* Placeholder for Checkbox Column: Perfect for a 'Clear All' button */}
            <th className="sticky-col" style={{ position: 'sticky', left: 0, top: '40px', zIndex: 59, backgroundColor: '#f9f9f9', borderBottom: '2px solid #bbb',
                /* --- SYNC TO FIXED WIDTH --- */
                '--col-width': `${CHECKBOX_WIDTH}px`,
                '--col-left': '0px'
             }}>
              <button onClick={handleClear} title="Clear all filters" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '10px' }}>
                🧹
              </button>
            </th>
            {cvl.map((col, idx) => {
              const isSticky = idx < 3;
              return (
              <th key={`f-${idx}`} className={isSticky ? "sticky-col" : ""}
                style={{
                  /* --- SYNC TO CSS VARIABLES --- */
                  '--col-width': `${colWidths[idx]}px`,
                  '--col-left': isSticky ? `${stickyLeft[idx]}px` : 'auto',
                  position: 'sticky', top: '40px', 
                  left: isSticky ? `${stickyLeft[idx]}px` : 'auto',
                  zIndex: isSticky ? 54 : 49, backgroundColor: '#f9f9f9', borderBottom: '2px solid #bbb', padding: '4px'
                }}
              >
                <TableHeaderFilter 
                  col={col} 
                  unitIndex={unitIndex} 
                  currentUnit={currentUnit} 
                  filters={filters} 
                  updateContext={updateContext} 
                />
              </th>
              );
            })}
          </tr>
        </thead>

        <tbody style={{ zIndex: 10 }}>
          {paginatedData.map((item,index) => ( // Determine if row is selected    
   
            <tr 
              key={item.row_id} 
              className={item.meta?.selected? "selected-row" : ""} 
              style={{ height: '25px' }}
            >
              <td className="sticky-col"  style={{ position: 'sticky', left: 0, zIndex: 20, backgroundColor: '#fff', borderBottom: '1px solid #eee', textAlign: 'center',
                /* --- SYNC TO CSS VARIABLES --- */
                backgroundColor: 'var(--bg-panel)',
                borderBottom: '1px solid var(--border-color)',
                /* --- SYNC TO FIXED WIDTH --- */
                '--col-width': `${CHECKBOX_WIDTH}px`,
                '--col-left': '0px'
               }}>
                <input type="checkbox" checked={!!item.meta?.selected} onChange={() => toggleRowSelection(item.row_id)} />
              </td>
              {cvl.map((col, idx) => {
                const isSticky = idx < 3;
                const isLocked = col.locked === true;
                return (
                    <TableCell 
                        key={col.name} // Essential for React list rendering

                        // --- Pass the styling props down ---
                        isSticky={isSticky}
                        stickyLeft={isSticky ? stickyLeft[idx] : 0}
                        colWidth={colWidths[idx]}

                        col={col} 
                        item={item} 
                        index={index} // Needed for your 'sequence' type logic
                        isSelected={isSelected(item.row_id, col.name)} // This triggers the re-render when moving arrows
                        onClick={handleCellClick} // Pass the stable reference
                        unitIndex={unitIndex}
                        currentUnit={currentUnit}
                        updateContext={updateContext}
                        triggerEvent={triggerEvent}
                        handleRowBlur={handleRowBlur}
                        tableId={tableId}
                        isLocked={isLocked}
                     />         
                );
              })}
            </tr>
))}
        </tbody>

        <tfoot style={{ position: 'sticky', bottom: 0, zIndex: 100, backgroundColor: '#eee' }}>
          {/* Totals Row */}
          <tr style={{ height: '35px', fontWeight: 'bold' }}>
            <td className="sticky-col" style={{ position: 'sticky', left: 0, backgroundColor: '#eee', borderTop: '2px solid #ccc',
                // backgroundColor: 'var(--bg-panel)',
                // borderBottom: '1px solid var(--border-color)',
                /* --- SYNC TO FIXED WIDTH --- */
                '--col-width': `${CHECKBOX_WIDTH}px`,
                '--col-left': '0px'
             }} />
            {cvl.map((col, idx) => {
              const isSticky = idx < 3;
              return (
                <td key={`t-${idx}`} className={isSticky ? "sticky-col" : ""}
                  style={{
                    position: 'sticky', left: isSticky ? stickyLeft[idx] : 'auto',
                    borderTop: '2px solid #ccc', padding: '8px',
                    /* --- SYNC TO CSS VARIABLES --- */
                    // backgroundColor: 'var(--bg-panel)',
                    // borderBottom: '1px solid var(--border-color)',
                    '--col-width': `${colWidths[idx]}px`,
                    '--col-left': isSticky ? `${stickyLeft[idx]}px` : 'auto'
                  }}
                >
                  {totals[idx]}
                </td>
              );
            })}
          </tr>
          {/* Pagination Row */}
          <tr>
            <td colSpan={cvl.length + 1} style={{ padding: 0, border: 'none' }}>
              <div style={{ 
                position: 'sticky', 
                left: 0, 
                width: '100%', 
                display: 'flex', 
                justifyContent: 'flex-end', 
                alignItems: 'center', 
                height: '45px', 
                padding: '0 20px', 
                /* --- THEME SYNC --- */
                background: 'var(--bg-panel)',    /* Dark: #1a1a1a | Light: #f8f9fa */
                color: 'var(--text-main)' 
              }}>
                <TablePagination unitId={tableId} totalItems={displayedData.length} />
              </div>
            </td>
          </tr>

        </tfoot>
      </table>
    </div>
  );
};

export default DynamicTable;
